// US-101662 Scenario 4: hard limits enforced by the upload flow. Backend
// repeats these checks (defense in depth) — keep both sides in sync.
export const ICON_MAX_SIZE_BYTES = 2 * 1024 * 1024; // 2 MB
export const ALLOWED_ICON_MIME_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml'] as const;
export const ALLOWED_ICON_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.svg'] as const;
export const RECENT_ICONS_LIMIT = 10;

export const mapIconServiceMessages = {
  FALLBACK_ERROR: 'Something went wrong',
  GET_MAP_ICONS: {
    ERROR: 'Unable to fetch Map Icons',
  },
  FETCH_ICON_URL: {
    ERROR: 'Unable to fetch Map Icons',
  },
  GET_PRESIGNED_URL: {
    ERROR: 'Unable to get Presigned URL',
  },
  UPLOAD_ICON: {
    SUCCESS: 'Icon uploaded successfully!',
    ERROR: 'Unable to upload Icon',
  },
  GET_RECENT_ICONS: {
    ERROR: 'Unable to fetch recently used icons',
  },
  VALIDATION: {
    // AC wording: must be exactly this.
    SIZE_EXCEEDED: 'File size exceeds the maximum limit of 2 MB',
    INVALID_FORMAT: 'Unsupported icon format. Allowed formats: PNG, JPG, SVG.',
  },
};

// Minimal shape we validate — a real File satisfies it, and tests can pass a
// plain object without constructing a Blob.
export interface IconFileLike {
  name: string;
  type: string;
  size: number;
}

// US-101662 Scenario 4: single source of truth for the upload gate. Returns the
// exact AC error message to surface, or null when the file is acceptable.
// Size is checked first (2 MB ceiling — exactly 2 MB is allowed, only "exceeds"
// fails), then MIME + extension (extension is the last-line check in case the
// browser hands us a generic octet-stream for an SVG/legacy file).
export const validateIconFile = (file: IconFileLike): string | null => {
  if (file.size > ICON_MAX_SIZE_BYTES) return mapIconServiceMessages.VALIDATION.SIZE_EXCEEDED;
  const lowerName = file.name.toLowerCase();
  const extOk = ALLOWED_ICON_EXTENSIONS.some((ext) => lowerName.endsWith(ext));
  const mimeOk = (ALLOWED_ICON_MIME_TYPES as readonly string[]).includes(file.type);
  if (!mimeOk || !extOk) return mapIconServiceMessages.VALIDATION.INVALID_FORMAT;
  return null;
};
